import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, Form, Input, message, Modal, Spin, Switch, Upload, Typography, Tabs, Checkbox } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { UploadFile } from 'antd/es/upload/interface';
import { Image as AntdImage } from 'antd';
import imageCompression from 'browser-image-compression';

import { getPromo, createPromo, updatePromo, deletePromo, uploadPromoImage } from 'src/features/adv/adv_promo.api';
import { RoleEnum } from 'src/features/users/users.types';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';

const SUPPORTED_LANGS = ['ru', 'en'];

interface AdvPromoTranslation {
  lang: string;
  promoTitle?: string;
  promoDescription?: string;
  modalTitle?: string;
  modalDescription?: string;
}

interface AdvPromoData {
  promo_visible: boolean;
  promo_preview_url: string;
  modal_preview_url?: string | null;
  show_at_session_start: boolean;
  translations: {
    [lang: string]: Omit<AdvPromoTranslation, 'lang'>;
  };
}

interface AdvPromoTypes {
  id: string;
  companyId: string;
  promoVisible: boolean;
  promoPreviewUrl: string;
  modalPreviewUrl: string | null;
  showAtSessionStart: boolean;
  translations: AdvPromoTranslation[];
}

interface AdvPromoFormValues {
  promo_visible: boolean;
  promo_preview_url: string;
  modal_preview_url?: string;
  show_at_session_start: boolean;
  translations: { [key: string]: any };
  promoTitle?: string;
  promoDescription?: string;
  modalTitle?: string;
  modalDescription?: string;
}

const CompanyAdvPromoTab: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const [promoForm] = Form.useForm<AdvPromoFormValues>();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [promoAdv, setPromoAdv] = useState<AdvPromoTypes | null>(null);
  const [uploading, setUploading] = useState(false);
  const [activeLang, setActiveLang] = useState<string>('ru');

  const fetchPromoAdv = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPromo({ companyId });
      if (response.status === 200) {
        setPromoAdv(response.data);
      } else if (response.status === 404) {
        setPromoAdv(null);
      } else {
        console.error('Server error on fetch:', response);
        message.error('Ошибка сервера');
        setPromoAdv(null);
      }
    } catch (error) {
      console.error('Failed to fetch promo advertisement:', error);
      message.error('Ошибка загрузки');
      setPromoAdv(null);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchPromoAdv();
  }, [fetchPromoAdv]);

  const setPromoFormValuesFromAdvAndLang = useCallback(
    (advertisement: AdvPromoTypes, lang: string) => {
      const translationsAsObject: { [key: string]: any } = {};
      SUPPORTED_LANGS.forEach(l => {
        translationsAsObject[l] = {
          promoTitle: '',
          promoDescription: '',
          modalTitle: '',
          modalDescription: '',
        };
      });
      if (Array.isArray(advertisement.translations)) {
        advertisement.translations.forEach(translation => {
          if (SUPPORTED_LANGS.includes(translation.lang)) {
            translationsAsObject[translation.lang] = {
              promoTitle: translation.promoTitle || '',
              promoDescription: translation.promoDescription || '',
              modalTitle: translation.modalTitle || '',
              modalDescription: translation.modalDescription || '',
            };
          }
        });
      }

      const currentTranslation = translationsAsObject[lang] || {};

      promoForm.setFieldsValue({
        promo_visible: advertisement.promoVisible,
        promo_preview_url: advertisement.promoPreviewUrl,
        modal_preview_url: advertisement.modalPreviewUrl || undefined,
        show_at_session_start: advertisement.showAtSessionStart ?? false,
        translations: translationsAsObject,
        promoTitle: currentTranslation.promoTitle || '',
        promoDescription: currentTranslation.promoDescription || '',
        modalTitle: currentTranslation.modalTitle || '',
        modalDescription: currentTranslation.modalDescription || '',
      });
    },
    [promoForm]
  );

  useEffect(() => {
    if (promoAdv && !editing) {
      const allLangs = promoAdv.translations?.map(t => t.lang) || [];
      const langToUse = allLangs.includes(activeLang) ? activeLang : allLangs[0] || 'ru';
      setActiveLang(langToUse);
      setPromoFormValuesFromAdvAndLang(promoAdv, langToUse);
    }
  }, [promoAdv, editing, activeLang, setPromoFormValuesFromAdvAndLang]);

  const handleLangChange = useCallback(
    (key: string) => {
      if (editing) {
        const currentValues = promoForm.getFieldsValue(true);
        const currentTranslations = { ...currentValues.translations };
        const currentLangData = { ...currentTranslations[activeLang] };

        currentLangData.promoTitle = currentValues.promoTitle || '';
        currentLangData.promoDescription = currentValues.promoDescription || '';
        currentLangData.modalTitle = currentValues.modalTitle || '';
        currentLangData.modalDescription = currentValues.modalDescription || '';

        currentTranslations[activeLang] = currentLangData;

        if (!currentTranslations[key]) {
          currentTranslations[key] = {
            promoTitle: '',
            promoDescription: '',
            modalTitle: '',
            modalDescription: '',
          };
        }

        const newValues: any = {
          translations: currentTranslations,
          promoTitle: currentTranslations[key].promoTitle || '',
          promoDescription: currentTranslations[key].promoDescription || '',
          modalTitle: currentTranslations[key].modalTitle || '',
          modalDescription: currentTranslations[key].modalDescription || '',
        };

        promoForm.setFieldsValue(newValues);
      }
      setActiveLang(key);
    },
    [activeLang, editing, promoForm]
  );

  const handleUpload = useCallback(
    async (file: UploadFile, assetType: 'promo' | 'modal') => {
      setUploading(true);
      try {
        let maxWidth = 1024;
        let maxHeight = 1024;

        if (assetType === 'promo') {
          maxWidth = 256;
          maxHeight = 256;
        } else if (assetType === 'modal') {
          maxWidth = 680;
          maxHeight = 2000;
        }

        const options = {
          maxSizeMB: 5,
          maxWidthOrHeight: Math.max(maxWidth, maxHeight),
          useWebWorker: true,
        };

        const fileAsFile = file as unknown as File;
        const compressedFile = await imageCompression(fileAsFile, options);

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Could not get canvas context');
        }

        const img = new Image();
        img.src = URL.createObjectURL(compressedFile);

        const resizedFile = await new Promise<File>((resolve, reject) => {
          img.onload = () => {
            let newWidth = img.width;
            let newHeight = img.height;

            if (assetType === 'modal') {
              if (newWidth <= maxWidth && newHeight <= maxHeight) {
                canvas.width = newWidth;
                canvas.height = newHeight;
                ctx.drawImage(img, 0, 0, newWidth, newHeight);
              } else {
                if (newWidth > maxWidth) {
                  newHeight *= maxWidth / newWidth;
                  newWidth = maxWidth;
                }
                if (newHeight > maxHeight) {
                  newWidth *= maxHeight / newHeight;
                  newHeight = maxHeight;
                }
                canvas.width = newWidth;
                canvas.height = newHeight;
                ctx.drawImage(img, 0, 0, newWidth, newHeight);
              }
            } else {
              if (newWidth > maxWidth) {
                newHeight *= maxWidth / newWidth;
                newWidth = maxWidth;
              }
              if (newHeight > maxHeight) {
                newWidth *= maxHeight / newHeight;
                newHeight = maxHeight;
              }
              canvas.width = newWidth;
              canvas.height = newHeight;
              ctx.drawImage(img, 0, 0, newWidth, newHeight);
            }

            canvas.toBlob(
              blob => {
                if (!blob) {
                  reject(new Error('Canvas toBlob failed'));
                  return;
                }
                const resizedFile = new File([blob], file.name, { type: blob.type });
                resolve(resizedFile);
              },
              file.type || 'image/jpeg',
              0.85
            );
          };
          img.onerror = reject;
        });

        const presignedData = await uploadPromoImage({
          companyId,
          filename: resizedFile.name,
          assetType,
        });

        if (!presignedData || !presignedData.fields || !presignedData.url) {
          throw new Error('Invalid presigned data');
        }

        const formData = new FormData();
        Object.keys(presignedData.fields).forEach(key => {
          formData.append(key, presignedData.fields[key]);
        });
        formData.append('file', resizedFile);

        const uploadResponse = await fetch(presignedData.url, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.statusText}`);
        }

        const s3Url = `https://storage.yandexcloud.net/viavr.global/${presignedData.fields.key}`.trim();

        promoForm.setFieldsValue({ [`${assetType}_preview_url`]: s3Url });
        if (promoAdv) {
          if (assetType === 'promo') {
            setPromoAdv({ ...promoAdv, promoPreviewUrl: s3Url });
          } else {
            setPromoAdv({ ...promoAdv, modalPreviewUrl: s3Url });
          }
        }

        message.success(`Изображение загружено и оптимизировано`);
      } catch (error) {
        message.error(`Ошибка загрузки: ${(error as Error).message}`);
      } finally {
        setUploading(false);
      }
    },
    [companyId, promoAdv, promoForm]
  );

  const handleRemoveImage = useCallback(
    (assetType: 'promo' | 'modal') => {
      Modal.confirm({
        title: 'Удалить изображение?',
        content: 'Это действие нельзя отменить.',
        onOk: () => {
          if (assetType === 'promo') {
            promoForm.setFieldsValue({ promo_preview_url: '' });
            if (promoAdv) {
              setPromoAdv({ ...promoAdv, promoPreviewUrl: '' });
            }
          } else {
            promoForm.setFieldsValue({ modal_preview_url: undefined });
            if (promoAdv) {
              setPromoAdv({ ...promoAdv, modalPreviewUrl: null });
            }
          }

          message.success('Изображение удалено');
        },
      });
    },
    [promoAdv, promoForm]
  );

  const handleSave = useCallback(async () => {
    if (uploading) {
      message.warning('Дождитесь загрузки изображений');
      return;
    }

    try {
      await promoForm.validateFields();
    } catch (error: any) {
      if (error?.errorFields) {
        message.error('Ошибка ввода: проверьте ограничения по длине и обязательные поля');
        return;
      }
      throw error;
    }

    const currentValues = promoForm.getFieldsValue(true);
    const currentTranslations = { ...currentValues.translations };
    const currentLangData = { ...currentTranslations[activeLang] };

    currentLangData.promoTitle = currentValues.promoTitle || '';
    currentLangData.promoDescription = currentValues.promoDescription || '';
    currentLangData.modalTitle = currentValues.modalTitle || '';
    currentLangData.modalDescription = currentValues.modalDescription || '';

    currentTranslations[activeLang] = currentLangData;
    promoForm.setFieldsValue({ translations: currentTranslations });

    let hasAtLeastOneCompleteLocale = false;
    for (const lang of SUPPORTED_LANGS) {
      const t = currentTranslations[lang] || {};
      const hasAnyField =
        (t.promoTitle && t.promoTitle.trim()) ||
        (t.promoDescription && t.promoDescription.trim()) ||
        (t.modalTitle && t.modalTitle.trim()) ||
        (t.modalDescription && t.modalDescription.trim());

      if (hasAnyField) {
        if (
          !t.promoTitle?.trim() ||
          !t.promoDescription?.trim() ||
          !t.modalTitle?.trim() ||
          !t.modalDescription?.trim()
        ) {
          message.error(`В локали "${lang.toUpperCase()}" заполнены не все обязательные поля.`);
          return;
        }
        hasAtLeastOneCompleteLocale = true;
      }
    }

    if (!hasAtLeastOneCompleteLocale) {
      message.error('Заполните хотя бы одну локализацию полностью ru или en.');
      return;
    }

    const finalValues = promoForm.getFieldsValue(true);
    const baseData: AdvPromoData = {
      promo_visible: finalValues.promo_visible || false,
      promo_preview_url: finalValues.promo_preview_url,
      modal_preview_url: finalValues.modal_preview_url || null,
      show_at_session_start: finalValues.show_at_session_start,
      translations: finalValues.translations || {},
    };

    try {
      let response;
      if (promoAdv) {
        response = await updatePromo({ companyId, data: baseData });
      } else {
        response = await createPromo({ companyId, data: baseData });
      }

      setPromoAdv(response.data);
      setEditing(false);
      message.success('Промо успешно сохранено');
    } catch (error: any) {
      console.error('Save API error:', error);
      if (error?.response?.status === 409) {
        message.error('Промо реклама для этой компании уже существует. Используйте кнопку "Редактировать".');
      } else {
        message.error('Ошибка сохранения');
      }
    }
  }, [activeLang, companyId, promoAdv, promoForm, uploading]);

  const handleDelete = useCallback(async () => {
    Modal.confirm({
      title: 'Удалить рекламу?',
      content: 'Это действие нельзя отменить.',
      onOk: async () => {
        try {
          await deletePromo({ companyId });
          message.success('Реклама удалена');
          setPromoAdv(null);
          setEditing(false);
          promoForm.resetFields();
        } catch (error) {
          message.error('Ошибка удаления');
        }
      },
    });
  }, [companyId, promoForm]);

  const handleAddClick = () => {
    const initialTranslations: Record<string, any> = {};
    SUPPORTED_LANGS.forEach(lang => {
      initialTranslations[lang] = {
        promoTitle: '',
        promoDescription: '',
        modalTitle: '',
        modalDescription: '',
      };
    });

    promoForm.setFieldsValue({
      promo_visible: false,
      promo_preview_url: '',
      modal_preview_url: undefined,
      show_at_session_start: false,
      translations: initialTranslations,
      promoTitle: '',
      promoDescription: '',
      modalTitle: '',
      modalDescription: '',
    });
    setEditing(true);
    setActiveLang('ru');
  };

  const currentPromoTranslation = React.useMemo(() => {
    if (promoAdv && Array.isArray(promoAdv.translations)) {
      const translation = promoAdv.translations.find(t => t.lang === activeLang);
      if (translation) {
        return {
          lang: activeLang,
          promoTitle: translation.promoTitle || '',
          promoDescription: translation.promoDescription || '',
          modalTitle: translation.modalTitle || '',
          modalDescription: translation.modalDescription || '',
        };
      }
    }
    return { lang: activeLang, promoTitle: '', promoDescription: '', modalTitle: '', modalDescription: '' };
  }, [promoAdv, activeLang]);

  if (loading) return <Spin />;

  if (!promoAdv && !editing) {
    return (
      <Card>
        <Typography.Title level={4}>Реклама в промо-акциях</Typography.Title>
        <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
          <div style={{ display: 'flex', gap: '12px', marginBottom: 16 }}>
            <Button type='primary' icon={<PlusOutlined />} onClick={handleAddClick}>
              Добавить рекламу
            </Button>
          </div>
        </ComponentRoleProtected>
      </Card>
    );
  }

  if (promoAdv && !Array.isArray(promoAdv.translations)) {
    return (
      <Card>
        <Typography.Title level={4}>Реклама в промо-акциях</Typography.Title>
        <Typography.Text type='danger'>Ошибка: Данные рекламы повреждены.</Typography.Text>
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4}>Реклама в промо-акциях</Typography.Title>
        <div>
          {editing ? (
            <>
              <Button onClick={() => setEditing(false)} style={{ marginRight: 8 }}>
                Отмена
              </Button>
              <Button type='primary' onClick={handleSave} loading={uploading}>
                Сохранить
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setEditing(true)} style={{ marginRight: 8 }}>
                Редактировать
              </Button>
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                Удалить
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs
        activeKey={activeLang}
        onChange={handleLangChange}
        items={SUPPORTED_LANGS.map(lang => ({ label: lang.toUpperCase(), key: lang }))}
        style={{ marginBottom: 24 }}
      />

      <Form form={promoForm} layout='vertical'>
        <Typography.Title level={5} style={{ marginBottom: 16 }}>
          Рекламная карточка
        </Typography.Title>

        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          {/* Превью карточки — ОБЯЗАТЕЛЬНОЕ */}
          {editing ? (
            <Form.Item
              label={<strong>Превью (256х256)</strong>}
              style={{ flex: '0 0 120px' }}
              name='promo_preview_url'
              rules={[{ required: true, message: 'Превью обязательно' }]}
            >
              <>
                {promoForm.getFieldValue('promo_preview_url') ? (
                  <AntdImage src={promoForm.getFieldValue('promo_preview_url')} width={120} />
                ) : null}
                <Upload
                  beforeUpload={file => {
                    handleUpload(file, 'promo');
                    return false;
                  }}
                  accept='image/*'
                  showUploadList={false}
                  disabled={uploading}
                >
                  <Button type='primary' size='small' style={{ marginTop: 8 }}>
                    {promoForm.getFieldValue('promo_preview_url') ? 'Обновить' : 'Загрузить'}
                  </Button>
                </Upload>
              </>
            </Form.Item>
          ) : (
            <div style={{ flex: '0 0 120px' }}>
              <div style={{ marginBottom: 4 }}>
                <strong>Превью (256х256)</strong>
              </div>
              {promoAdv?.promoPreviewUrl ? (
                <AntdImage src={promoAdv.promoPreviewUrl} width={120} />
              ) : (
                <Typography.Text type='secondary'>—</Typography.Text>
              )}
            </div>
          )}

          {/* Поля внутри карточки */}
          <div style={{ flex: 1 }}>
            {editing ? (
              <>
                <Form.Item
                  label={<strong>Заголовок</strong>}
                  name='promoTitle'
                  rules={[
                    { required: true, message: 'Заголовок обязателен' },
                    { max: 60, message: 'Заголовок должен быть не более 60 символов' },
                  ]}
                >
                  <Input placeholder='Введите заголовок' />
                </Form.Item>

                <Form.Item label={<strong>Отображать в шлеме</strong>} name='promo_visible' valuePropName='checked'>
                  <Switch />
                </Form.Item>

                <Form.Item
                  label={<strong>Описание</strong>}
                  name='promoDescription'
                  rules={[
                    { required: true, message: 'Описание обязательно' },
                    { max: 256, message: 'Описание должно быть не более 256 символов' },
                  ]}
                >
                  <Input.TextArea rows={2} placeholder='Введите описание' />
                </Form.Item>
              </>
            ) : (
              <>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ marginBottom: 4 }}>
                    <strong>Заголовок</strong>
                  </div>
                  <Typography.Text>{currentPromoTranslation.promoTitle || '—'}</Typography.Text>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <div style={{ marginBottom: 4 }}>
                    <strong>Отображать в шлеме</strong>
                  </div>
                  <Typography.Text>{promoAdv?.promoVisible ? 'Да' : 'Нет'}</Typography.Text>
                </div>

                <div>
                  <div style={{ marginBottom: 4 }}>
                    <strong>Описание</strong>
                  </div>
                  <Typography.Text>{currentPromoTranslation.promoDescription || '—'}</Typography.Text>
                </div>
              </>
            )}
          </div>
        </div>

        <Typography.Title level={5} style={{ marginBottom: 16 }}>
          Модальное окно
        </Typography.Title>

        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          {/* Превью модалки — НЕОБЯЗАТЕЛЬНОЕ */}
          {editing ? (
            <Form.Item
              label={<strong>Превью (680×...)</strong>}
              style={{ flex: '0 0 120px' }}
              name='modal_preview_url'
              rules={[{ required: false }]}
            >
              {promoForm.getFieldValue('modal_preview_url') ? (
                <>
                  <AntdImage src={promoForm.getFieldValue('modal_preview_url')} width={120} />
                  <Button danger size='small' onClick={() => handleRemoveImage('modal')} style={{ marginTop: 8 }}>
                    Удалить
                  </Button>
                </>
              ) : (
                <Upload
                  beforeUpload={file => {
                    handleUpload(file, 'modal');
                    return false;
                  }}
                  accept='image/*'
                  showUploadList={false}
                  disabled={uploading}
                >
                  <Button type='primary' size='small' style={{ marginTop: 8 }}>
                    Загрузить
                  </Button>
                </Upload>
              )}
            </Form.Item>
          ) : (
            <div style={{ flex: '0 0 120px' }}>
              <div style={{ marginBottom: 4 }}>
                <strong>Превью (680×...)</strong>
              </div>
              {promoAdv?.modalPreviewUrl ? (
                <AntdImage src={promoAdv.modalPreviewUrl} width={120} />
              ) : (
                <Typography.Text type='secondary'>—</Typography.Text>
              )}
            </div>
          )}

          {/* Заголовок и описание модалки — ОБЯЗАТЕЛЬНЫЕ */}
          <div style={{ flex: 1 }}>
            {editing ? (
              <>
                <Form.Item
                  label={<strong>Заголовок</strong>}
                  name='modalTitle'
                  rules={[
                    { required: true, message: 'Заголовок обязательно' },
                    { max: 100, message: 'Заголовок должен быть не более 100 символов' },
                  ]}
                >
                  <Input placeholder='Введите заголовок' />
                </Form.Item>
                <Form.Item
                  label={<strong>Описание</strong>}
                  name='modalDescription'
                  rules={[
                    { required: true, message: 'Описание обязательно' },
                    { max: 8000, message: 'Описание должен быть не более 8000 символов' },
                  ]}
                >
                  <Input.TextArea rows={3} placeholder='Введите описание не более 8000 символов' />
                </Form.Item>
                <Form.Item name='show_at_session_start' valuePropName='checked' style={{ marginBottom: 0 }}>
                  <Checkbox>Открыть в начале сессии</Checkbox>
                </Form.Item>
              </>
            ) : (
              <>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ marginBottom: 4 }}>
                    <strong>Заголовок</strong>
                  </div>
                  <Typography.Text>{currentPromoTranslation.modalTitle || '—'}</Typography.Text>
                </div>
                <div>
                  <div style={{ marginBottom: 4 }}>
                    <strong>Описание</strong>
                  </div>
                  <Typography.Text>{currentPromoTranslation.modalDescription || '—'}</Typography.Text>
                </div>
                <div style={{ marginTop: 8 }}>
                  <Typography.Text type={promoAdv?.showAtSessionStart ? 'success' : 'secondary'}>
                    {promoAdv?.showAtSessionStart ? '✓ Открыть в начале сессии' : 'Не открывать в начале сессии'}
                  </Typography.Text>
                </div>
              </>
            )}
          </div>
        </div>
      </Form>
    </Card>
  );
};

export default CompanyAdvPromoTab;
